import React, {FC, useMemo, useState} from "react";
import {Alert, Button, Input, Select, Space, Table, Typography} from "antd";
import type {TableColumnsType} from "antd";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import dayjs from "dayjs";
import {
    EventOfferBalanceDtoFilterInput,
    EventOfferBalanceDtoSortInput,
    EventOfferBalancesQuery,
    EventOfferStatus,
    SortEnumType,
    useEventOfferBalancesQuery
} from "../../GraphQL/generated/graphql";
import {PageHeader, Panel, StatusTag} from "../../Components/ui";
import {formatLocalDate, formatMoney, humanizeEnumValue} from "../../format";
import {normalizeEnumKey} from "../../i18n/labelHelpers";
import {eventParticipantRoute} from "./eventWorkspaceRoutes";
import {useSelectedEventWorkspace} from "./EventWorkspaceLayout";
import {EMPTY_OPERATIONS_VALUE, operationalOfferStatusTone, operationalPaymentStatusTone} from "./participantOperationsHelpers";
import RecordPaymentDialog from "./RecordPaymentDialog";

export type EventOfferBalance = NonNullable<NonNullable<EventOfferBalancesQuery["eventOfferBalances"]>["items"]>[number];
export type EventPaymentsFilter = "current" | "outstanding" | "paid" | "all";
type SortColumn = "offer" | "participant" | "payer" | "amount" | "outstanding" | "dueDate";
export type EventPaymentsSort = {columnKey: SortColumn; order: "ascend" | "descend"} | null;

const PAGE_SIZE = 50;
const isClosedOffer = (balance: EventOfferBalance) => ["EXPIRED", "REPLACED"].includes(normalizeEnumKey(balance.status));
const outstanding = (balance: EventOfferBalance) => Number(balance.outstandingAmount) || 0;

// Expired offers stay in "current" because they may still carry a payment history or a balance to chase.
export const buildEventPaymentsWhere = (filter: EventPaymentsFilter, search: string): EventOfferBalanceDtoFilterInput | undefined => {
    const conditions: EventOfferBalanceDtoFilterInput[] = [];
    if (filter !== "all") conditions.push({status: {neq: EventOfferStatus.Replaced}});
    if (filter === "outstanding") conditions.push({outstandingAmount: {gt: 0}});
    if (filter === "paid") conditions.push({outstandingAmount: {lte: 0}});
    const term = search.trim();
    if (term) conditions.push({or: [
        {offerNumber: {contains: term}},
        {participantFullName: {contains: term}},
        {payerName: {contains: term}}
    ]});
    return conditions.length ? {and: conditions} : undefined;
};

export const buildEventPaymentsOrder = (sort: EventPaymentsSort): EventOfferBalanceDtoSortInput[] => {
    if (!sort) return [{generatedAt: SortEnumType.Desc}, {id: SortEnumType.Desc}];
    const direction = sort.order === "ascend" ? SortEnumType.Asc : SortEnumType.Desc;
    const field: Record<SortColumn, EventOfferBalanceDtoSortInput> = {
        offer: {offerNumber: direction},
        participant: {participantFullName: direction},
        payer: {payerName: direction},
        amount: {grossTotal: direction},
        outstanding: {outstandingAmount: direction},
        dueDate: {dueDate: direction}
    };
    return [field[sort.columnKey], {id: SortEnumType.Desc}];
};

export const canRecordEventPayment = (balance: EventOfferBalance) =>
    !isClosedOffer(balance) && outstanding(balance) > 0;

const balanceStatus = (balance: EventOfferBalance) => {
    if (outstanding(balance) <= 0) return {key: "event-payments.balance-paid", paymentStatus: "PAID"};
    if ((Number(balance.paidAmount) || 0) > 0) return {key: "event-payments.balance-partial", paymentStatus: "PENDING"};
    return {key: "event-payments.balance-unpaid", paymentStatus: undefined};
};

const EventPaymentsPage: FC = () => {
    const {t} = useTranslation();
    const enumText = (value?: string | null) =>
        value ? t(`enums:${value}`, {defaultValue: humanizeEnumValue(value)}) : EMPTY_OPERATIONS_VALUE;
    const {eventId} = useSelectedEventWorkspace();
    const [filter, setFilter] = useState<EventPaymentsFilter>("current");
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState<EventPaymentsSort>(null);
    const [page, setPage] = useState(1);
    const [recording, setRecording] = useState<EventOfferBalance | null>(null);
    const query = useEventOfferBalancesQuery({
        variables: {
            eventId,
            skip: (page - 1) * PAGE_SIZE,
            take: PAGE_SIZE,
            where: buildEventPaymentsWhere(filter, search),
            order: buildEventPaymentsOrder(sort)
        },
        fetchPolicy: "cache-and-network"
    });

    const balances = useMemo(() => (query.data?.eventOfferBalances?.items ?? [])
        .filter((item): item is EventOfferBalance => !!item), [query.data?.eventOfferBalances?.items]);
    const today = dayjs().startOf("day");

    const columns: TableColumnsType<EventOfferBalance> = [
        {
            key: "offer",
            title: t("event-payments.offer"),
            sorter: true,
            render: (_, balance) => <Typography.Text strong>{balance.offerNumber || balance.id}</Typography.Text>
        },
        {
            key: "participant",
            title: t("event-payments.participant"),
            sorter: true,
            render: (_, balance) => <Link to={eventParticipantRoute(eventId, balance.participantId)}>
                {balance.participantFullName || EMPTY_OPERATIONS_VALUE}</Link>
        },
        {
            key: "payer",
            title: t("event-payments.payer"),
            sorter: true,
            render: (_, balance) => balance.payerName || EMPTY_OPERATIONS_VALUE
        },
        {
            key: "type",
            title: t("event-payments.type"),
            render: (_, balance) => enumText(balance.type)
        },
        {
            key: "status",
            title: t("event-payments.status"),
            render: (_, balance) => <StatusTag tone={operationalOfferStatusTone(balance.status)}>
                {enumText(balance.status)}</StatusTag>
        },
        {
            key: "amount",
            title: t("event-payments.amount"),
            align: "right",
            sorter: true,
            render: (_, balance) => formatMoney(balance.grossTotal, balance.currency)
        },
        {
            key: "paid",
            title: t("event-payments.paid"),
            align: "right",
            render: (_, balance) => formatMoney(balance.paidAmount, balance.currency)
        },
        {
            key: "outstanding",
            title: t("event-payments.outstanding"),
            align: "right",
            sorter: true,
            render: (_, balance) => {
                const status = balanceStatus(balance);
                return <Space direction="vertical" size={2} align="end">
                    <Typography.Text strong>{formatMoney(balance.outstandingAmount, balance.currency)}</Typography.Text>
                    <StatusTag tone={operationalPaymentStatusTone(status.paymentStatus)}>{t(status.key)}</StatusTag>
                </Space>;
            }
        },
        {
            key: "dueDate",
            title: t("event-payments.due-date"),
            sorter: true,
            render: (_, balance) => {
                const overdue = !isClosedOffer(balance) && outstanding(balance) > 0 && dayjs(balance.dueDate).isBefore(today);
                return <Space direction="vertical" size={2}>
                    {formatLocalDate(balance.dueDate, EMPTY_OPERATIONS_VALUE)}
                    {overdue && <StatusTag tone="warning">{t("event-payments.overdue")}</StatusTag>}
                </Space>;
            }
        },
        {
            key: "actions",
            title: t("event-payments.actions"),
            render: (_, balance) => <Space wrap>
                {canRecordEventPayment(balance) &&
                    <Button size="small" onClick={() => setRecording(balance)}>{t("payment-entry-title")}</Button>}
                <Link to={`/Admin/Payers/${balance.payerId}`}>{t("event-payments.open-payer")}</Link>
            </Space>
        }
    ];

    return (
        <div className="ati-workspace-page">
            <PageHeader level={2} title={t("event-workspace.payments")}/>
            {query.error && <Alert type="error" showIcon message={t("event-payments.load-failed")}/>}
            <Panel
                aria-label={t("event-workspace.payments")}
                extra={<Space wrap>
                    <Input.Search allowClear placeholder={t("event-payments.search")} aria-label={t("event-payments.search")}
                                  onSearch={value => {setSearch(value); setPage(1);}} style={{width: 280}}/>
                    <Select<EventPaymentsFilter> value={filter} onChange={value => {setFilter(value); setPage(1);}} aria-label={t("event-payments.filter")}
                                                 style={{width: 220}} options={[
                        {value: "current", label: t("event-payments.filter-current")},
                        {value: "outstanding", label: t("event-payments.filter-outstanding")},
                        {value: "paid", label: t("event-payments.filter-paid")},
                        {value: "all", label: t("event-payments.filter-all")}
                    ]}/>
                </Space>}
            >
                <Table<EventOfferBalance>
                    rowKey="id"
                    size="middle"
                    loading={query.loading}
                    columns={columns}
                    dataSource={balances}
                    pagination={{current: page, pageSize: PAGE_SIZE, total: query.data?.eventOfferBalances?.totalCount ?? 0,
                        showSizeChanger: false, hideOnSinglePage: true}}
                    onChange={(pagination, _filters, sorter) => {
                        const single = Array.isArray(sorter) ? sorter[0] : sorter;
                        const nextSort: EventPaymentsSort = single?.order && typeof single.columnKey === "string"
                            ? {columnKey: single.columnKey as SortColumn, order: single.order} : null;
                        if (JSON.stringify(nextSort) !== JSON.stringify(sort)) {setSort(nextSort); setPage(1);}
                        else setPage(pagination.current ?? 1);
                    }}
                    scroll={{x: 1100}}
                    locale={{emptyText: t("event-payments.empty")}}
                />
            </Panel>
            {recording && <RecordPaymentDialog
                key={recording.id}
                payerId={recording.payerId}
                initialOfferId={recording.id}
                offers={[recording]}
                onClose={() => setRecording(null)}
                onSaved={() => query.refetch()}
            />}
        </div>
    );
};

export default EventPaymentsPage;
